'use client'

// React Imports
import { useState, useMemo } from 'react'

// MUI Imports
import Card from '@mui/material/Card'
import CardHeader from '@mui/material/CardHeader'
import CardContent from '@mui/material/CardContent'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import { useTheme } from '@mui/material/styles'
import Skeleton from '@mui/material/Skeleton'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

// Recharts Imports
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from '@/libs/Recharts'

// Types
import type { MonthlyFinancialChartProps, MonthlyFinancialFlowData } from '@/types/dashboards/resumeTypes'

// Hooks
import { useDashboardResumeTranslations, useTranslations } from '@/hooks/useTranslations'



// Componente Skeleton
const FinancialChartSkeleton = () => {
  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Skeleton variant="text" width={200} height={28} sx={{ mb: 1 }} />
          <Skeleton variant="text" width={150} height={20} />
        </Box>
        <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: 1 }} />
      </Box>
      <Skeleton variant="rectangular" width="100%" height={320} sx={{ borderRadius: 1 }} />
    </Box>
  )
}

const MonthlyFinancialChart = ({ data, loading = false }: MonthlyFinancialChartProps) => {
  const { t } = useDashboardResumeTranslations()
  const { t: commonT } = useTranslations()
  const theme = useTheme()

  // Encontrar o índice do mês atual nos dados da API.
  // A API retorna 24 meses (12 passados + 12 futuros), o mês atual estará no meio.
  const currentMonthIndex = useMemo(() => {
    if (!data || data.length === 0) return 11

    return Math.floor(data.length / 2) - 1
  }, [data])

  // Dados padrão para desenvolvimento/fallback. Memoizados para evitar nova
  // referência a cada render — recharts dispatcha no chartDataContext e referência
  // nova a cada render loopa o subscription/notify (`Maximum update depth exceeded`).
  const defaultData = useMemo<MonthlyFinancialFlowData[]>(() => [
    { month: commonT('months.jan'), monthIndex: '0', availableBalance: 1850000, upcomingPayments: 320000, accountReceivable: 450000 },
    { month: commonT('months.feb'), monthIndex: '1', availableBalance: 1650000, upcomingPayments: 380000, accountReceivable: 520000 },
    { month: commonT('months.mar'), monthIndex: '2', availableBalance: 1420000, upcomingPayments: 420000, accountReceivable: 380000 },
    { month: commonT('months.apr'), monthIndex: '3', availableBalance: 1280000, upcomingPayments: 480000, accountReceivable: 650000 },
    { month: commonT('months.may'), monthIndex: '4', availableBalance: 1150000, upcomingPayments: 350000, accountReceivable: 580000 },
    { month: commonT('months.jun'), monthIndex: '5', availableBalance: 1320000, upcomingPayments: 290000, accountReceivable: 720000 },
    { month: commonT('months.jul'), monthIndex: '6', availableBalance: 1480000, upcomingPayments: 340000, accountReceivable: 620000 },
    { month: commonT('months.aug'), monthIndex: '7', availableBalance: 1380000, upcomingPayments: 390000, accountReceivable: 580000 },
    { month: commonT('months.sep'), monthIndex: '8', availableBalance: 1250000, upcomingPayments: 420000, accountReceivable: 650000 },
    { month: commonT('months.oct'), monthIndex: '9', availableBalance: 1180000, upcomingPayments: 380000, accountReceivable: 590000 },
    { month: commonT('months.nov'), monthIndex: '10', availableBalance: 1350000, upcomingPayments: 450000, accountReceivable: 680000 },
    { month: commonT('months.dec'), monthIndex: '11', availableBalance: 1200000, upcomingPayments: 520000, accountReceivable: 720000 }
  ], [commonT])

  // Mapear dados para incluir tradução dos meses.
  const financialData = useMemo(() => (
    (data || defaultData).map(item => ({
      ...item,
      month: commonT(`months.${item.month}`) || item.month
    }))
  ), [data, defaultData, commonT])

  // Estados - usando mesmo formato do RevenueVsExpensesChart
  const [period, setPeriod] = useState<'6m' | '12m' | '24m'>('12m')

  // Handler para mudança de período
  const handlePeriodChange = (
    event: React.MouseEvent<HTMLElement>,
    newPeriod: '6m' | '12m' | '24m'
  ) => {
    if (newPeriod !== null) {
      setPeriod(newPeriod)
    }
  }

  // Filtrar dados baseado no período selecionado, centralizando no mês atual
  const filteredData = useMemo(() => {
    if (!financialData || financialData.length === 0) {
      return []
    }

    const totalMonths = financialData.length
    const current = data ? currentMonthIndex : Math.floor(totalMonths / 2) - 1

    if (period === '24m') {
      return financialData // Retorna todos os dados (24 meses)
    } else if (period === '12m') {
      // 12 meses: 6 anteriores + atual + 5 posteriores
      const start = Math.max(0, current - 5)
      const end = Math.min(totalMonths, current + 7)

      return financialData.slice(start, end)
    } else if (period === '6m') {
      // 6 meses: 3 anteriores + atual + 2 posteriores
      const start = Math.max(0, current - 2)
      const end = Math.min(totalMonths, current + 4)

      return financialData.slice(start, end)
    }

    return financialData
  }, [financialData, period, currentMonthIndex, data])

  // Calcular estatísticas
  const totalBalance = filteredData[filteredData.length - 1]?.availableBalance || 0
  const totalUpcoming = filteredData.reduce((sum, item) => sum + item.upcomingPayments, 0)

  // Componente de tooltip customizado
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      // Determinar se é um mês futuro (previsto) baseado no índice do mês atual
      const itemIndex = payload[0]?.payload?.originalIndex ?? payload[0]?.payload?.monthIndex ?? -1
      const isForecast = parseInt(itemIndex) > currentMonthIndex
      const currentYear = new Date().getFullYear()
      const yearSuffix = String(currentYear).slice(-2) // Últimos 2 dígitos do ano

      return (
        <Card sx={{ p: 2, boxShadow: 3, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            {label} - {yearSuffix}
            {isForecast && (
              <Typography component="span" variant="caption" sx={{ ml: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                ({t('financialChart.forecast')})
              </Typography>
            )}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: entry.dataKey === 'availableBalance' ? '50%' : 1,
                  bgcolor: entry.color
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {entry.name}:
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {new Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                  notation: 'compact'
                }).format(entry.value)}
              </Typography>
            </Box>
          ))}
        </Card>
      )
    }

    return null
  }

  if (loading) {
    return (
      <Card sx={{ height: '100%', minHeight: 400 }}>
        <FinancialChartSkeleton />
      </Card>
    )
  }

  return (
    <Card sx={{ height: '100%', minHeight: 400 }}>
      <CardHeader
        title={
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
            {t('financialChart.title')}
          </Typography>
        }
        subheader={
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
            {t('financialChart.balance')}: {new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
              notation: 'compact'
            }).format(totalBalance)} • {t('financialChart.payments')}: {new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
              notation: 'compact'
            }).format(totalUpcoming)}
          </Typography>
        }
        action={
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={handlePeriodChange}
            size="small"
            sx={{ height: 32 }}
          >
            <ToggleButton value="6m" sx={{ px: 2 }}>
              6M
            </ToggleButton>
            <ToggleButton value="12m" sx={{ px: 2 }}>
              12M
            </ToggleButton>
            <ToggleButton value="24m" sx={{ px: 2 }}>
              24M
            </ToggleButton>
          </ToggleButtonGroup>
        }
        sx={{ pb: 1 }}
      />
      <CardContent sx={{ pt: 0 }}>
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart
            data={filteredData}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 20
            }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="var(--mui-palette-divider)"
              horizontal={true}
              vertical={false}
            />
            <XAxis
              dataKey="month"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: 'var(--mui-palette-text-disabled)' }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: 'var(--mui-palette-text-disabled)' }}
              tickFormatter={(value) =>
                new Intl.NumberFormat('pt-BR', {
                  notation: 'compact',
                  minimumFractionDigits: 0
                }).format(value)
              }
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              verticalAlign="top"
              height={36}
              iconType="rect"
              wrapperStyle={{
                paddingBottom: '20px',
                fontSize: '14px',
                color: theme.palette.text.primary
              }}
            />
            <Bar
              dataKey="upcomingPayments"
              fill={theme.palette.error.main}
              name={t('financialChart.upcomingPayments')}
              radius={[2, 2, 0, 0]}
              opacity={0.8}
            />
            <Bar
              dataKey="accountReceivable"
              fill={theme.palette.success.main}
              name={t('financialChart.accountsReceivable')}
              radius={[2, 2, 0, 0]}
              opacity={0.8}
            />
            {/* Saldo Disponível */}
            <Line
              type="monotone"
              dataKey="availableBalance"
              stroke={theme.palette.info.main}
              strokeWidth={3}
              dot={{ fill: theme.palette.info.main, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: theme.palette.info.main, strokeWidth: 2 }}
              name={t('financialChart.availableBalance')}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}

export default MonthlyFinancialChart
